import React, { FC, Fragment } from "react";
import { useIntl } from "react-intl";
import { useHistory } from "react-router";
import { PersonalizationReducerState } from "src/store/personalization/types";
import useUserById from "../../../hooks/useUserById";
import { useStopImpersonatingCallback } from "../../../store/impersonation/useActions";
import { useImpersonatedUserId } from "../../../store/impersonation/useSelectors";
import { useLogoutUserAction } from "../../../store/login/hooks/useActions";
import { useUser } from "../../../store/login/hooks/useSelectors";
import { useUserInfoData } from "../../../store/user/useSelectors";
import LogOutSection from "./LogOutSection";

type Props = {
  invokeBugReportModal: () => void;
  invokeBugOverviewModal: () => void;
  invokeFeedbackModal: () => void;
  invokePasswordChangeModal: () => void;

  unreportedBugsCount: number;
  hasFlowioBugs: boolean;
  personalizationData: PersonalizationReducerState | null;
};

const Container: FC<Props> = ({
  invokeFeedbackModal,
  invokeBugReportModal,
  invokeBugOverviewModal,
  invokePasswordChangeModal,
  unreportedBugsCount,
  personalizationData,
  hasFlowioBugs,
}) => {
  const history = useHistory();
  const { formatMessage: fm } = useIntl();

  const user = useUser();
  const { userName } = useUserInfoData();
  const requestLogout = useLogoutUserAction();

  const impersonatedUserId = useImpersonatedUserId();
  const impersonatedUser = useUserById(impersonatedUserId ?? null);
  const impersonatedName = (() => {
    if (impersonatedUserId === null) return null;
    if (impersonatedUser?.userName) return impersonatedUser.userName;
    return fm({ id: "common.user" }).concat(` #${impersonatedUserId}`);
  })();

  const stopImpersonating = useStopImpersonatingCallback();

  const hardRefresh = () => {
    document.write("");
    history.push("/");
    window.location.reload();
  };

  const handleLogout = async () => {
    await requestLogout();
    hardRefresh();
  };

  const handleStopImpersonating = async () => {
    await stopImpersonating();
    hardRefresh();
  };

  return (
    <Fragment>
      <LogOutSection
        user={user ? { ...user, userName } : null}
        requestLogout={handleLogout}
        personalizationData={personalizationData}
        stopImpersonating={handleStopImpersonating}
        nameOfImpersonatedUser={impersonatedName}
        unreportedBugsCount={unreportedBugsCount}
        hasFlowioBugs={hasFlowioBugs}
        invokeBugReportModal={invokeBugReportModal}
        invokeFeedbackModal={invokeFeedbackModal}
        invokePasswordChangeModal={invokePasswordChangeModal}
        invokeBugsOverviewModal={invokeBugOverviewModal}
      />
    </Fragment>
  );
};

export default Container;
